import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/apiService';
import './ForgetPassword.css';

function ForgetPassword() {
    const { t } = useTranslation();
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const [token, setToken] = useState('');
    const [userInfo, setUserInfo] = useState('');
    const navigate = useNavigate();

    const delay = (ms) =>
        new Promise((resolve) => {
            setTimeout(resolve, ms);
        });

    const getStepDescription = () => {
        switch (step) {
            case 1:
                return t('forgot_step_phone');
            case 2:
                return t('forgot_step_code');
            case 3:
                return t('forgot_step_password');
            default:
                return '';
        }
    };

    const persianToEnglishDigits = (str) => {
        if (!str) return '';
        const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
        let english = str;
        for (let i = 0; i < 10; i += 1) {
            english = english.replace(persianNumbers[i], i);
        }
        return english;
    };

    const handleNumericInput = (setter) => (e) => {
        const { value } = e.target;
        const numericRegex = /^[0-9۰-۹]*$/;
        if (numericRegex.test(value)) {
            setter(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const englishPhone = persianToEnglishDigits(phone);
            const englishCode = persianToEnglishDigits(code);

            if (step === 1) {
                const data = await authAPI.requestPasswordOtp(englishPhone);
                if (data.message === 'OTP sent successfully via SMS.') {
                    setSnack({
                        open: true,
                        message: t('otp_sent_success'),
                        severity: 'success',
                    });
                    setStep(2);
                } else {
                    throw new Error(data.message || t('otp_send_failed'));
                }
            } else if (step === 2) {
                const data = await authAPI.validateOtp({
                    otp: englishCode,
                    destination: englishPhone,
                    due_type: 'ResetPass',
                });

                if (data.message === 'OTP validated successfully.') {
                    setSnack({
                        open: true,
                        message: t('otp_validated_success'),
                        severity: 'success',
                    });
                    setUserInfo(data.user_info);
                    setToken(data.token);
                    setStep(3);
                } else {
                    throw new Error(data.message || t('otp_invalid'));
                }
            } else if (step === 3) {
                if (newPassword !== confirmPassword) {
                    setSnack({
                        open: true,
                        message: t('passwords_not_match'),
                        severity: 'error',
                    });
                    return;
                }

                const data = await authAPI.resetPassword({
                    new_password: newPassword,
                    confirm_new_password: confirmPassword,
                    token,
                    user_info: userInfo,
                });
                if (data.message === 'Password reset successfully.') {
                    setSnack({
                        open: true,
                        message: t('password_reset_success'),
                        severity: 'success',
                    });
                    await delay(3000);
                    navigate('/login');
                } else {
                    throw new Error(data.message || t('password_reset_failed'));
                }
            }
        } catch (err) {
            setSnack({
                open: true,
                message: err.message || t('unknown_error'),
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnack = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnack((prev) => ({ ...prev, open: false }));
    };

    const renderButtonText = () => {
        if (loading) return t('sending');
        if (step === 1) return t('send_code');
        if (step === 2) return t('verify_code');
        return t('change_password');
    };

    const renderFormContent = () => {
        switch (step) {
            case 1:
                return (
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9۰-۹]*"
                        placeholder={t('phone_number')}
                        value={phone}
                        onChange={handleNumericInput(setPhone)}
                        className="forgot-input"
                        required
                        aria-label={t('phone_number')}
                    />
                );
            case 2:
                return (
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9۰-۹]*"
                        placeholder={t('verification_code')}
                        value={code}
                        onChange={handleNumericInput(setCode)}
                        className="forgot-input"
                        required
                        aria-label={t('verification_code')}
                    />
                );
            case 3:
                return (
                    <>
                        <div className="passwordWrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t('new_password')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="passwordInput"
                                required
                                aria-label={t('new_password')}
                            />
                            <button
                                type="button"
                                className="passwordToggle"
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </button>
                        </div>
                        <input
                            type="password"
                            placeholder={t('repeat_new_password')}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="passwordInput"
                            required
                            aria-label={t('repeat_new_password')}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="forgot-page">
            <div className="forgot-form-container">
                <h2 className="forgot-title">{t('forgot_password_title')}</h2>
                <p className="forgot-text">{getStepDescription()}</p>

                <form className="forgot-form" onSubmit={handleSubmit}>
                    {renderFormContent()}

                    <div className="forgot-buttons">
                        <button
                            type="button"
                            className="forgot-btn-back"
                            onClick={() => {
                                if (step === 2) {
                                    setStep(1);
                                    setCode('');
                                } else {
                                    navigate('/login');
                                }
                            }}
                        >
                            {t('back')}
                        </button>

                        <button type="submit" className="forgot-btn-send" disabled={loading}>
                            {renderButtonText()}
                        </button>
                    </div>
                </form>
            </div>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={handleCloseSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    severity={snack.severity}
                    sx={{ width: '100%' }}
                >
                    {snack.message}
                </MuiAlert>
            </Snackbar>
            <div className="forgot-left" />
        </div>
    );
}

export default ForgetPassword;
